import { BUILDERS_ADDON_NOTICE_KEY, parseDismissedOrgIds, withDismissedOrg } from "../billing/builders-addon";

// Presentation preferences ONLY. Per the hard boundary, localStorage never holds
// sessions, CSRF, tokens, or PII — just non-sensitive display prefs.
const KEYS = {
  lastOrgId: "epidev.lastOrgId",
  lastAdminPath: "epidev.lastAdminPath",
  compactTables: "epidev.compactTables",
  theme: "epidev.theme",
  buildersAddonNotice: BUILDERS_ADDON_NOTICE_KEY
} as const;

export type AdminLandingPath =
  | "/admin/entitlements"
  | "/admin/revenue"
  | "/admin/organizations"
  | "/admin/audit"
  | "/admin/discord"
  | "/admin/contracts"
  | "/admin/contract-queue";

/** Best-effort storage read: denied or missing storage reads as "not set". */
export function readPref(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** Best-effort storage write: denied storage is swallowed, never thrown. */
export function writePref(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch {
    /* private mode / disabled storage — prefs are best-effort */
  }
}

function readAdminPath(): AdminLandingPath {
  const value = readPref(KEYS.lastAdminPath);
  return value === "/admin/organizations" || value === "/admin/revenue" || value === "/admin/audit" || value === "/admin/discord"
    || value === "/admin/contracts" || value === "/admin/contract-queue"
    ? value
    : "/admin/entitlements";
}

class Prefs {
  lastOrgId = $state<string | null>(readPref(KEYS.lastOrgId));
  lastAdminPath = $state<AdminLandingPath>(readAdminPath());
  compactTables = $state<boolean>(readPref(KEYS.compactTables) === "1");
  theme = $state<"dark">("dark");

  setLastOrg(orgId: string | null): void {
    this.lastOrgId = orgId;
    writePref(KEYS.lastOrgId, orgId);
  }

  setLastAdminPath(path: AdminLandingPath): void {
    this.lastAdminPath = path;
    writePref(KEYS.lastAdminPath, path);
  }

  toggleCompact(): void {
    this.compactTables = !this.compactTables;
    writePref(KEYS.compactTables, this.compactTables ? "1" : "0");
  }

  /**
   * Organizations whose Overview Builders add-on notice was dismissed in this browser.
   * The in-memory list is what hides the notice, so a dismissal still holds for the
   * session when storage is denied; storage only carries it across reloads.
   */
  dismissedBuildersAddonOrgs = $state<string[]>(parseDismissedOrgIds(readPref(KEYS.buildersAddonNotice)));

  isBuildersAddonNoticeDismissed(orgId: string): boolean {
    return this.dismissedBuildersAddonOrgs.includes(orgId);
  }

  dismissBuildersAddonNotice(orgId: string): void {
    this.dismissedBuildersAddonOrgs = withDismissedOrg(this.dismissedBuildersAddonOrgs, orgId);
    // Merge with what another tab may have stored since this one loaded, so one tab's
    // dismissal never erases another's.
    let stored = parseDismissedOrgIds(readPref(KEYS.buildersAddonNotice));
    for (const id of this.dismissedBuildersAddonOrgs) stored = withDismissedOrg(stored, id);
    writePref(KEYS.buildersAddonNotice, JSON.stringify(stored));
  }

  /** Re-read the persisted dismissals (a fresh page load does this implicitly). */
  syncBuildersAddonNotice(): void {
    this.dismissedBuildersAddonOrgs = parseDismissedOrgIds(readPref(KEYS.buildersAddonNotice));
  }
}

export const prefs = new Prefs();
